import React, { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import { fmtDate, fmtDateTime, fmtDay, fmtMoney, statusTag, urgencyDot, toLocalInput } from '../lib/format';
import SwipeToDelete from '../components/SwipeToDelete';
import ClientProfile from '../components/ClientProfile';
import { ledgerTotals, credentialTag, expiryPhrase } from '../lib/ledger';
import LedgerCard from '../components/LedgerCard';
import PlatformRevenue from '../components/PlatformRevenue';
import { copyPayLink } from '../lib/payLink';
import { LeadCard, LeadsBoard, ViewSwitch, STAGES, DROP_OPENS, DROP_SETS } from '../components/LeadViews';
import MessageButton from '../components/MessageButton';
import InstallCard from '../components/InstallCard';
import AdminProjectDetail from '../components/AdminProjectDetail';
import FollowThrough from '../components/FollowThrough';
import VendorDocuments from '../components/VendorDocuments';
import Marketing from '../components/Marketing';
import WorkEditor from './WorkEditor';
import CardMenu, { MenuIcons } from '../components/CardMenu';
import ThemeToggle from '../components/ThemeToggle';
import Wordmark from '../components/Wordmark';
import MediaStrip from '../components/MediaStrip';
import DocumentList from '../components/DocumentList';
import { vendorMenuItems, dialable, smsHref } from '../lib/cardActions';
import { TAXONOMY } from './VendorDashboard';
import { docWho, docAbout } from '../lib/docSummary';

// Groups vendors into board columns. ONE card per technician, in the column
// of his main trade. Saul ticked ten trades on his application and the board
// drew him ten times, so a roster of five people read as fourteen companies.
// Anyone with no trade on file lands in a catch-all column so they never
// disappear.
// Line icons for the header. Inline rather than an icon package: three
// 20px glyphs are not worth a dependency, and `currentColor` lets the button's
// hover state colour them without a second rule.
const Ico = {
  mail: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </svg>
  ),
  key: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="8" cy="15" r="4" />
      <path d="M10.85 12.15L19 4M17 6l2 2M15 8l2 2" />
    </svg>
  ),
  home: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 10.5L12 3l9 7.5" />
      <path d="M5.5 9.5V20h13V9.5" />
    </svg>
  ),
  inbox: (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 13h5l1.5 3h5L16 13h5" />
      <path d="M5.5 5h13l2.5 8v5a1 1 0 01-1 1H4a1 1 0 01-1-1v-5z" />
    </svg>
  ),
};

// The bento-box empty state. A list with nothing in it used to render as
// blank space below a filter bar, which reads as a page that failed to load.
// This says which list is empty, why, and what to do about it.
function EmptyState({ icon, title, subtitle, actionLabel, onAction }) {
  return (
    <div className="qps-empty-state-card">
      <div className="qps-empty-icon-wrapper">{icon || Ico.inbox}</div>
      <div className="qps-empty-title">{title}</div>
      {subtitle && <div className="qps-empty-subtitle">{subtitle}</div>}
      {actionLabel && onAction && (
        <button type="button" className="qps-btn-primary" onClick={onAction}>{actionLabel}</button>
      )}
    </div>
  );
}

const NO_TRADE = 'No trade on file';

// Where one technician's card lives: the main trade he was filed under, or
// the first trade he ticked. Nobody had to go and set this for the whole
// roster — an application with Framing ticked first files him under Framing,
// and Julian moves the card if that is wrong.
const homeTradeOf = (v) => {
  const trades = tradesOf(v);
  const main = (v.profile?.primaryTrade || '').trim();
  if (main && trades.includes(main)) return main;
  return trades[0] || NO_TRADE;
};

// One card per technician, plus the names of everyone else who can do that
// trade. Both halves matter: the card stops Saul reading as ten companies,
// and the names keep the thing the board is actually for — you scan Drywall,
// and it tells you who can hang drywall even though they are filed elsewhere.
function vendorColumns(vendors) {
  const cols = new Map();
  const col = (trade) => {
    if (!cols.has(trade)) cols.set(trade, { cards: [], also: [] });
    return cols.get(trade);
  };

  vendors.forEach((v) => {
    const home = homeTradeOf(v);
    col(home).cards.push(v);
    tradesOf(v).forEach((tr) => { if (tr !== home) col(tr).also.push(v); });
  });

  // Watched first (that's what the eye is for), then whoever covers the most
  // trades — the one-stop techs are worth seeing before you assemble a crew.
  const rank = (a, b) => (b.watched ? 1 : 0) - (a.watched ? 1 : 0)
    || tradesOf(b).length - tradesOf(a).length;
  cols.forEach((c) => { c.cards.sort(rank); c.also.sort(rank); });

  // Busiest trades first, counting everyone who can do the work rather than
  // only the cards: a column where four people cover the trade is worth
  // reading before one where a single card sits alone.
  const weight = (c) => c.cards.length * 2 + c.also.length;
  return [...cols.entries()]
    .sort((a, b) => (a[0] === NO_TRADE) - (b[0] === NO_TRADE)
      || weight(b[1]) - weight(a[1])
      || a[0].localeCompare(b[0]));
}

// Traffic light for a vendor card. The server already works out whether their
// paperwork is current, expiring inside 90 days, or lapsed — this just turns
// that into the colour Julian reads at a glance:
//   green  = everything in date, good to send work to
//   yellow = a renewal is coming up, chase it before it becomes a problem
//   red    = expired or missing, can't be used until it's fixed
const VENDOR_LIGHTS = {
  valid:    { edge: 'var(--green)', tint: 'var(--green-light)', text: 'var(--green)', label: 'Good to use — paperwork current' },
  expiring: { edge: 'var(--amber)', tint: 'var(--amber-light)', text: 'var(--amber)', label: 'Renewal coming up' },
  blocked:  { edge: 'var(--red)',   tint: 'var(--red-light)',   text: 'var(--red)',   label: "Can't use — paperwork not current" },
};
const lightFor = (v) => VENDOR_LIGHTS[v.compliance?.status] || VENDOR_LIGHTS.blocked;

// Column headings use the full trade name; the chips on a card can't — four of
// these are long enough to wrap a card three lines deep on a phone.
const SHORT_TRADE = {
  'Interior and Exterior Painting': 'Painting',
  'Demolition and Junk Removal': 'Demolition',
  'Remodeling & Construction': 'Remodeling',
  'General Repairs': 'Repairs',
};
const shortTrade = (t) => SHORT_TRADE[t] || t;

// Every trade a vendor said they can do, in the order they picked them.
const tradesOf = (v) => {
  const list = [...(v.profile?.expertise || [])];
  if (v.profile?.expertiseOther) list.push(v.profile.expertiseOther);
  return list;
};

// One vendor card on the board — name, phone, and the labels that matter at a
// glance. Tapping it opens that vendor's full profile (jobs, ledger, progress).
function VendorCard({ v, onOpen, menuItems, column, onDragStart, onDragEnd, dragging }) {
  const p = v.profile || {};
  const light = lightFor(v);
  const soonest = (v.compliance?.items || [])
    .filter(i => i.status === 'expiring')
    .sort((a, b) => a.daysLeft - b.daysLeft)[0];
  const trades = tradesOf(v);

  return (
    <div
      className={`vcard${dragging ? ' is-dragging' : ''}`}
      style={{ borderLeftColor: light.edge, background: light.tint }}
      onClick={() => onOpen(v)}
      // Mouse drag only — HTML5 drag events don't fire on touch, which is why
      // "Move card" also lives in the menu for phone use.
      draggable={Boolean(onDragStart)}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <CardMenu items={menuItems(v, column)} label={`Actions for ${v.businessName}`} />
      <div className="vcard-name">
        {v.watched && <span className="vcard-watch" title="Watching">{MenuIcons.eye}</span>}
        {v.businessName}
      </div>
      <div className="vcard-meta">{v.phone || v.userId?.phone || 'no phone on file'}</div>

      {/* Every trade this tech can do, not just the column you're standing in.
          This is the whole point of the board: you scan a trade column, and the
          card tells you the same person also covers electrical and drywall — so
          one tech takes the whole job instead of three showing up. The column
          you're in is filled; the rest are outlined. */}
      {trades.length > 0 && (
        <div className="vcard-trades">
          {trades.map(t => (
            <span key={t} className={`vtrade${t === column ? ' here' : ''}`}>{shortTrade(t)}</span>
          ))}
          {trades.length > 1 && (
            <span className="vtrade-count">covers {trades.length} trades</span>
          )}
        </div>
      )}

      <div className="vcard-labels">
        <span className="vcard-label" style={{ background: 'var(--white)', color: light.text }}>{light.label}</span>
        {soonest && (
          <span className="vcard-label" style={{ background: 'var(--white)', color: light.text }}>
            {soonest.label}: {expiryPhrase(soonest.daysLeft)}
          </span>
        )}
        {!p.completedAt && <span className="vcard-label" style={{ background: 'var(--white)', color: 'var(--muted)' }}>Application pending</span>}
        {p.hasCrew === 'Yes' && <span className="vcard-label" style={{ background: 'var(--white)', color: 'var(--teal)' }}>Has crew</span>}
      </div>
    </div>
  );
}

// +13855383625 reads as a serial number. (385) 538-3625 reads as a phone.
const prettyPhone = (v) => {
  const d = String(v || '').replace(/\D/g, '');
  const ten = d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
  if (ten.length !== 10) return String(v || '');
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
};

// Technicians who share a phone number, which on this roster means one person
// entered more than once. It names what each record is carrying, because that
// is the only thing that decides which one to keep.
function DuplicateVendors({ vendors, ledger, onOpen }) {
  const digits = (v) => String(v || '').replace(/\D/g, '').slice(-10);

  const groups = [];
  const byPhone = new Map();
  (vendors || []).forEach((v) => {
    const key = digits(v.phone || v.userId?.phone);
    if (key.length !== 10) return;           // no phone is not a match
    if (!byPhone.has(key)) byPhone.set(key, []);
    byPhone.get(key).push(v);
  });
  byPhone.forEach((list, phone) => { if (list.length > 1) groups.push({ phone, list }); });
  if (groups.length === 0) return null;

  // What a record holds, in the words that matter when choosing.
  const holds = (v) => {
    const mine = (ledger || []).filter(e => (e.vendorId?._id || e.vendorId) === v._id);
    const paid = mine.reduce((t, e) => t + (e.payments || []).reduce((n, p) => n + (Number(p.amount) || 0), 0), 0);
    const papers = (v.credentials || []).filter(c => c.documentUrl).length;
    const bits = [];
    if (mine.length) bits.push(`${mine.length} invoice${mine.length === 1 ? '' : 's'}`);
    if (paid > 0) bits.push(`${fmtMoney(paid)} paid`);
    if (papers) bits.push(`${papers} document${papers === 1 ? '' : 's'}`);
    return bits.length ? bits.join(' · ') : 'nothing on it yet';
  };

  return (
    <div className="alert alert-amber" style={{ margin: '0 0 16px' }}>
      <div style={{ width: '100%' }}>
        <strong>The same technician is on the roster more than once.</strong>
        <div style={{ marginTop: 6, lineHeight: 1.55, fontSize: 13 }}>
          These records share a phone number, so they are one person entered
          twice. Their work and their paperwork are split across them. Open
          each one to see what it holds before you decide which to keep —
          deleting the wrong one takes an invoice with it.
        </div>
        {groups.map(g => (
          <div key={g.phone} style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 700 }}>{g.phone}</div>
            {g.list.map(v => (
              <button
                key={v._id}
                type="button"
                className="btn btn-ghost btn-sm"
                style={{ display: 'block', textAlign: 'left', width: '100%', padding: '6px 0' }}
                onClick={() => onOpen(v)}
              >
                <span style={{ fontWeight: 700 }}>{v.businessName}</span>
                <span style={{ color: 'var(--muted)' }}> — {holds(v)}</span>
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const {
    currentScreen, goTo, logout, activeTab, setActiveTab, requests,
    setActiveRequest, setModal, setAssignVendorId, handleApproveInvoice,
    handleAdminApproveEstimate, openEstimate,
    openWorkOrder, isRaisingWorkOrder, setPrintDoc,
    setAppointmentInput, adminView, setAdminView, clients, vendors,
    setConfirmDelete,
    ledger, ledgerVendorFilter, setLedgerVendorFilter,
    openLedgerForm, handleDeleteLedgerEntry, handleExportLedger,
    openRecordPayment, handleDeletePayment, openProgress, openCredential,
    activeVendor, setActiveVendor, vendorLayout, setVendorLayout,
    projects, activeProject, setActiveProject, estimates,
    openProjectForm, handleDeleteProject, handleApproveEstimateToProject, handleDeclineEstimate,
    handleToggleWatch, handleMoveVendorTrade, showToast, openVendorEdit, openLicenseCheck,
    agentBoard, loadAgentBoard,
    // The Our Work tab needs the count for its badge; the editor itself
    // reads the rest of these off the context directly.
    workAll, loadWorkAll,
    revealed, setRevealed, revealPayout, revealW9,
    promoteClientToAccount, openLeadQualify, reorderEstimates, openStatementInvoice,
    storage,
    openInvoicePaper, openEstimatePaper,
    techEstimates, loadTechEstimates,
    loadAdmins, handleSendSignInLink,
    smsStatus, refreshSmsStatus, isLoadingSms,
    adminAccounts, loadAdminAccounts, adminAccountDetail, openAdminAccount,
    openAccountForm, openAccountMemberForm, handleRemoveAccountMember, openAdminPropertyForm,
    adminPropertyId, setAdminPropertyId, openPropertyJobForm, openAdminInvoice,
    authHeaders,
    openJobCustomer, openJobInvoiceDoc, setLeadStatus, openLeadDoc,
    addAdminPropertyDocuments, removeAdminPropertyDocument, addJobDocuments,
    pushOn, pushBusy, togglePush, sendTestPush, pushBlockedReason, isInstalled,
    openAddClientForm, openClientForm, openVendorAdd,
    activeClient, setActiveClient, openLeadForClient,
    openEstimateForClient, openClientInvoice, openInvoiceFromEstimate, openInvoiceDoc,
    askDeleteEstimate, askDeleteInvoice,
  } = useApp();
  // Board, list or tiles. Remembered, because somebody who picked a way to
  // look at their work should not have to pick it again every morning.
  const [leadView, setLeadView] = useState(() => {
    try { return localStorage.getItem('qps.leadView') || 'list'; } catch { return 'list'; }
  });
  const pickLeadView = (v) => {
    setLeadView(v);
    try { localStorage.setItem('qps.leadView', v); } catch { /* private window */ }
  };

  // Dropping a card into a column. Julian's call: a stage that is the RESULT
  // of something opens the thing that does it, and the card moves when that
  // finishes. Nothing here quietly emails a customer or marks money received
  // because a card was dragged two inches.
  // Everything a job can do, in one list, behind the three lines at the top
  // left of the card. Built once and handed to every view: a row of nine
  // buttons under each card was unreadable, and three copies of that row
  // would be three places to forget the next button.
  const actionsFor = (req) => [
    // First, because it is what clicking the card already does and a menu
    // whose top item is not the obvious one teaches people to read all of it.
    { key: 'open', label: 'Open document', icon: MenuIcons.doc, onSelect: () => openLeadDoc(req) },
    { key: 'customer', label: 'Edit customer', icon: MenuIcons.pencil, onSelect: () => openJobCustomer(req) },
    // Agent 8 named the questions; this is where the answers go in. Second
    // on the list because on a brand new enquiry it is the next thing that
    // happens — before an appointment and long before an estimate.
    {
      key: 'qualify',
      label: req.lead?.missing?.length
        ? `Qualify · ${req.lead.missing.length} to ask`
        : 'Qualifying answers',
      icon: MenuIcons.doc,
      onSelect: () => openLeadQualify(req),
    },
    { key: 'appt', label: req.appointmentAt ? 'Reschedule' : 'Set appointment', icon: MenuIcons.doc, onSelect: () => openAppointment(req) },
    { key: 'est-write', label: req.estimate?.amount ? 'View / edit estimate' : 'Write estimate', icon: MenuIcons.pencil, onSelect: () => openEstimate(req) },
    { key: 'est-print', label: 'View & print estimate', icon: MenuIcons.printer, hidden: !req.estimate?.amount, onSelect: () => setPrintDoc({ request: req, kind: 'estimate' }) },
    { key: 'approve', label: 'Approve estimate for customer', icon: MenuIcons.doc, hidden: req.estimateStatus !== 'pending', onSelect: () => handleAdminApproveEstimate(req, true) },
    { key: 'decline', label: 'Mark estimate declined', icon: MenuIcons.trash, hidden: req.estimateStatus !== 'pending', onSelect: () => handleAdminApproveEstimate(req, false) },
    { key: 'convert', label: 'Convert estimate to invoice', icon: MenuIcons.doc, hidden: !req.estimate?.amount || req.invoice?.amount != null, onSelect: () => openAdminInvoice(req, 'estimate') },
    { key: 'inv-write', label: req.invoice?.amount != null ? 'View / edit invoice' : 'Write invoice', icon: MenuIcons.pencil, onSelect: () => openAdminInvoice(req) },
    { key: 'inv-pdf', label: 'Invoice PDF', icon: MenuIcons.printer, hidden: req.invoice?.amount == null, onSelect: () => openJobInvoiceDoc(req) },
    { key: 'pay', label: 'Copy pay link', icon: MenuIcons.doc, hidden: req.invoice?.amount == null || req.invoiceStatus === 'paid', onSelect: () => sharePayLink(req) },
    { key: 'approve-inv', label: `Approve invoice (${fmtMoney(req.invoice?.amount)})`, icon: MenuIcons.doc, hidden: req.status !== 'invoiced', onSelect: () => handleApproveInvoice(req._id) },
    {
      key: 'assign',
      label: req.assignedVendorId ? 'Reassign tech' : 'Assign tech',
      icon: MenuIcons.doc,
      hidden: ['completed', 'invoiced', 'paid'].includes(req.status),
      onSelect: () => { setActiveRequest(req); setAssignVendorId(String(req.assignedVendorId?._id || req.assignedVendorId || req.requestedVendorId || '')); setModal('assign'); },
    },
    { key: 'wo', label: req.workOrder?.number ? `Work order ${req.workOrder.number}` : 'Create work order', icon: MenuIcons.doc, onSelect: () => openWorkOrder(req) },
    { key: 'wo-del', label: 'Delete work order', icon: MenuIcons.trash, danger: true, hidden: !req.workOrder?.number, onSelect: () => setConfirmDelete({ type: 'work order', id: req._id, label: req.workOrder?.number }) },
    // The way to move a job on a phone, where the browser's drag and drop does
    // nothing at all. Picking the column off a list also beats dragging a card
    // across a screen whose far edge you cannot see.
    ...STAGES
      .filter(st => st.key !== req.status && (DROP_OPENS.includes(st.key) || DROP_SETS.includes(st.key)))
      .map(st => ({ key: `mv-${st.key}`, label: `Move to ${st.label}`, icon: MenuIcons.doc, onSelect: () => moveLead(req, st.key) })),
    { key: 'del', label: 'Delete lead', icon: MenuIcons.trash, danger: true, onSelect: () => setConfirmDelete({ type: 'lead', id: req._id, label: `${req.homeownerId?.name || req.fullName}${req.serviceType ? ' · ' + req.serviceType : ''}` }) },
  ];

  const moveLead = (req, stage) => {
    if (stage === 'assigned') {
      setActiveRequest(req);
      setAssignVendorId(String(req.assignedVendorId?._id || req.assignedVendorId || req.requestedVendorId || ''));
      setModal('assign');
      return;
    }
    if (stage === 'invoiced') { openAdminInvoice(req, req.estimate?.amount ? 'estimate' : undefined); return; }
    if (stage === 'estimate-sent') { openEstimate(req); return; }
    if (stage === 'approved') {
      if (req.estimateStatus !== 'pending') {
        alert('There is no estimate waiting on this job. Write one, and approving it moves the card here.');
        return;
      }
      if (window.confirm(`Approve this estimate on behalf of ${req.fullName}? Their portal will say you approved it.`)) {
        handleAdminApproveEstimate(req, true);
      }
      return;
    }
    // What is left is bookkeeping: nothing is sent and nothing is charged.
    setLeadStatus(req, stage);
  };

  // The customer's pay link for a job, onto the clipboard so it can be pasted
  // into a text. Mints one on jobs billed before online payment existed.
  const sharePayLink = async (r) => {
    try {
      const { copied } = await copyPayLink({ requestId: r._id, token: r.payToken, auth: authHeaders() });
      if (copied) showToast('Pay link copied. Paste it into a text or email.');
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };


  // Opening a technician pulls their estimates, drafts included, so a support
  // call can start with the document on screen rather than with "read me the
  // first line". The queue itself still never shows a draft.
  const openVendorId = adminView === 'techs' ? activeVendor?._id : null;
  useEffect(() => {
    loadTechEstimates(openVendorId);
    // loadTechEstimates is rebuilt every render; listing it here would fetch forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openVendorId]);

  // Which project each waiting estimate is pointed at, before Approve is
  // pressed. Keyed by estimate id: two can be waiting at once and they must
  // not share one selection.
  const [approveTo, setApproveTo] = useState({});

  // { vendor, from } while the "Move card" picker is open.
  const [moving, setMoving] = useState(null);
  // The card currently being dragged, and the column being hovered over.
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const baseMenu = {
    onOpen: setActiveVendor,
    onEdit: openVendorEdit,
    onWatch: handleToggleWatch,
    onDelete: (v) => setConfirmDelete({ type: 'vendor', id: v._id, label: v.businessName }),
    showToast,
  };
  // Board cards can be moved — they're in a column, so "move" has a meaning.
  const menuItems = vendorMenuItems({
    ...baseMenu,
    onMove: (v, from) => setMoving({ vendor: v, from }),
  });
  // Move works from a list row as well now. It used to be left off because a
  // tech with two trades had no single column to move out of; one card per
  // person means he always has exactly one.
  const listMenuItems = vendorMenuItems({
    ...baseMenu,
    onMove: (v) => setMoving({ vendor: v, from: homeTradeOf(v) }),
  });

  const dropOnColumn = (trade) => {
    setDragOver(null);
    if (!dragging) return;
    const { vendor, from } = dragging;
    setDragging(null);
    if (trade === from || trade === NO_TRADE) return;
    handleMoveVendorTrade(vendor, from === NO_TRADE ? null : from, trade);
  };

  const openAppointment = (req) => {
    setActiveRequest(req);
    setAppointmentInput(toLocalInput(req.appointmentAt));
    setModal('appointment');
  };

  // The leads on screen, in the order they should be worked. Computed here
  // rather than inline in the JSX so the list can be counted before it is
  // drawn — an empty result needs an empty state, and a chain ending in .map()
  // cannot tell you it produced nothing.
  //
  // Emergencies first, then anything blocked waiting on a manager's approval,
  // then newest. The list is otherwise in date order, and an emergency filed
  // on Tuesday would sit below Wednesday's dripping tap.
  const leadRank = (r) => (r.priority === 'emergency' ? 0 : r.nteStatus === 'exceeded' ? 1 : r.priority === 'urgent' ? 2 : 3);
  const visibleRequests = requests
    .filter(r => (activeTab === 'all' ? true : r.status === activeTab))
    .slice()
    .sort((a, b) => leadRank(a) - leadRank(b) || new Date(b.createdAt) - new Date(a.createdAt));

  const visibleLedger = ledgerVendorFilter
    ? ledger.filter(e => (e.vendorId?._id || e.vendorId) === ledgerVendorFilter)
    : ledger;
  const totals = ledgerTotals(visibleLedger);
  // Vendors who can't be paid right now — surfaced on the ledger so it's clear
  // before a check gets cut.
  const blockedVendors = vendors.filter(v => v.compliance && !v.compliance.canBePaid);

  return (
    <div className={`screen qps-admin ${currentScreen === 'screen-admin-dashboard' ? 'active' : ''}`}>
      {/* Sticky, because the segmented control below scrolls away with the
          content and the way back to another view should not scroll away too. */}
      <header className="qps-admin-header">
        <div className="qps-logo-group">
          <button type="button" className="qps-logo-text" onClick={() => goTo('screen-home')} aria-label="QPS Hub home">
            <Wordmark size={20} />
          </button>
          <span className="qps-logo-dot" aria-hidden="true" />
          <span className="qps-logo-badge">Admin</span>
        </div>

        {/* Email and Password were full-width buttons competing with Logout for
            a phone's top row. They are settings you open twice a year, so they
            keep their place and lose their weight. Both carry a label and a
            title: an icon alone is a guess for anyone who has not used this. */}
        <div className="qps-nav-right">
          <ThemeToggle className="qps-icon-btn" />
          <button type="button" className="qps-icon-btn" title="Change your email" aria-label="Change your email" onClick={() => setModal('email')}>{Ico.mail}</button>
          <button type="button" className="qps-icon-btn" title="Change your password" aria-label="Change your password" onClick={() => setModal('password')}>{Ico.key}</button>
          {/* Admins. Next to the two buttons that already change who you are
              and how you get in, because it is the same kind of thing. */}
          <button type="button" className="qps-icon-btn" title="Admins" aria-label="Admins"
            onClick={() => { loadAdmins(); setModal('admins'); }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></svg>
          </button>
          <button type="button" className="qps-icon-btn" title="Public site" aria-label="Public site" onClick={() => goTo('screen-home')}>{Ico.home}</button>
          <button type="button" className="qps-logout-btn" onClick={logout}>Log out</button>
        </div>
      </header>

      <div className="qps-admin-workspace">

      {/* Seven views, one iOS segmented control. The strip scrolls sideways on
          a phone rather than clipping Texting off the end, and every view
          carries its count so the numbers read without changing screens.
          Written as data rather than seven near-identical buttons — the old
          version had the class string typed out seven times, and a new view
          meant copying a line and remembering to change three things in it. */}
      {(() => {
        const VIEWS = [
          // First on the bar on purpose. Every other tab is a list of things
          // that exist; this is the list of things that have stopped moving,
          // and it is the only one that goes stale by being ignored.
          // One badge, not two. A tab carrying both a count and an alert wraps
          // onto a second line in this control, and how many of them are
          // waiting on Julian is the first thing the board itself says anyway.
          { key: 'chasing',  label: 'Chasing',  count: agentBoard?.tally?.jobs ?? null,  onPick: () => loadAgentBoard() },
          // Called "Work Order Status" on screen, `leads` underneath. The key
          // is wired through adminView, the URL and a dozen conditions in
          // this file; renaming it would be a rename of plumbing nobody sees
          // in exchange for nothing. The LABEL is what Julian reads.
          { key: 'leads',    label: 'Work Order Status', count: requests.length,         onPick: () => {} },
          { key: 'clients',  label: 'Clients',  count: clients.length,                   onPick: () => setActiveClient(null) },
          { key: 'techs',    label: 'Vendors',  count: vendors.length,                   onPick: () => setActiveVendor(null),
            // Vendors whose licence or insurance is not current. Red, because
            // it is the one number here that costs money if ignored.
            alert: blockedVendors.length },
          { key: 'ledger',   label: 'Ledger',   count: ledger.length,                    onPick: () => {} },
          { key: 'projects', label: 'Projects', count: projects.length,                  onPick: () => setActiveProject(null) },
          { key: 'accounts', label: 'Accounts', count: (adminAccounts || []).length,     onPick: () => loadAdminAccounts() },
          { key: 'texting',  label: 'Texting',  count: null,                             onPick: () => refreshSmsStatus() },
          { key: 'marketing', label: 'Marketing', count: null,                           onPick: () => {} },
          // The public portfolio page. `count` is what is LIVE, not what
          // exists: a tab reading 9 while the website shows 2 is a tab that
          // lies about the thing it is there to manage.
          { key: 'work',     label: 'Our Work', count: (workAll || []).filter(w => w.published).length,
            onPick: () => loadWorkAll() },
        ];
        return (
          <div className="qps-segment-control-container" role="tablist" aria-label="Admin views">
            {VIEWS.map(v => (
              <button
                key={v.key}
                type="button"
                role="tab"
                aria-selected={adminView === v.key}
                className={`qps-segment-btn ${adminView === v.key ? 'active' : ''}`}
                onClick={() => { setAdminView(v.key); v.onPick(); }}
              >
                {v.label}
                {v.count !== null && <span className="qps-counter-badge">{v.count}</span>}
                {v.alert > 0 && <span className="qps-counter-badge alert" title="Paperwork not current">{v.alert}</span>}
              </button>
            ))}
          </div>
        );
      })()}

      {adminView === 'texting' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <div className="qps-sheet-title">Inbound texts</div>
            <button className="btn btn-outline btn-sm" onClick={refreshSmsStatus} disabled={isLoadingSms}>
              {isLoadingSms ? 'Checking…' : 'Refresh'}
            </button>
          </div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14, lineHeight: 1.5 }}>
            {/* The relay's own number, from the server, not typed in here. The
                office line was hardcoded into this sentence and it is not the
                number Twilio sends and receives on — so this screen told
                Julian to watch a phone the relay never touches. */}
            Every text sent to {smsStatus.number ? prettyPhone(smsStatus.number) : 'the QPS Hub number'} and
            what happened to it. If a reply is missing from a job thread, it is
            on this list with the reason.
          </div>

          <div style={{ marginBottom: 14, fontSize: 13 }}>
            Relay is {smsStatus.enabled
              ? <span style={{ color: 'var(--green)' }}>switched on</span>
              : <span style={{ color: 'var(--red)' }}>off — the Twilio keys are missing in Render</span>}
          </div>

          <InstallCard />

          {/* Push notifications: the ones that arrive with the site shut. */}
          <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 14, marginBottom: 18, background: 'var(--white)' }}>
            <div className="qps-sheet-title">
              Notifications on this device
            </div>
            {pushBlockedReason() ? (
              <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
                {pushBlockedReason()}
              </div>
            ) : (
              <>
                <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 10, lineHeight: 1.5 }}>
                  {pushOn
                    ? 'This device will buzz when a message arrives, even with the site closed.'
                    : 'Get alerted when a customer or technician messages you, even when the site is closed.'}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className={`btn btn-sm ${pushOn ? 'btn-ghost' : 'btn-primary'}`} disabled={pushBusy} onClick={togglePush}>
                    {pushBusy ? 'Working…' : pushOn ? 'Turn off' : 'Turn on notifications'}
                  </button>
                  {pushOn && (
                    <button className="btn btn-outline btn-sm" onClick={sendTestPush}>Send me a test</button>
                  )}
                </div>
              </>
            )}
            {/* Only reachable on Android or a computer — iPhone without the app
                installed is a hard block above, and with it installed there is
                nothing left to suggest. So this is a genuine option, not a
                requirement, and shouldn't be worded like one. */}
            {!isInstalled() && !pushBlockedReason() && (
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 10, lineHeight: 1.5 }}>
                Notifications work right here, no install needed. You can also add
                the site to your home screen for an app icon and a cleaner window.
              </div>
            )}
          </div>

          {smsStatus.loaded && smsStatus.events?.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, border: '1px solid var(--border)', borderRadius: 12, padding: 16, background: 'var(--white)' }}>
              Nothing has arrived yet. If you have texted the number and this stays
              empty, Twilio is not calling the website at all — check the Messaging
              Service in Twilio (Integration → Send a webhook) and make sure it
              posts to <code>https://qpshub.com/api/messages/sms-inbound</code>.
              Setting it on the phone number instead of the Messaging Service looks
              right in the console but does nothing.
            </div>
          )}

          {smsStatus.events?.map(e => {
            const good = e.outcome === 'filed';
            const label = {
              filed: 'Saved to the thread',
              bad_signature: 'Rejected — webhook URL mismatch',
              not_configured: 'Texting was switched off',
              empty: 'Empty message',
              no_match: 'Number not recognised',
              no_job: 'No job to file it against',
              error: 'Error',
            }[e.outcome] || e.outcome;
            return (
              <div key={e._id} style={{ border: '1px solid var(--border)', borderLeft: `3px solid ${good ? 'var(--green)' : 'var(--red)'}`, borderRadius: 10, padding: '10px 12px', marginBottom: 8, background: 'var(--white)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 14 }}>{e.from || 'unknown number'}</span>
                  <span style={{ fontSize: 12, color: good ? 'var(--green)' : 'var(--red)' }}>{label}</span>
                </div>
                {e.bodyPreview && <div style={{ fontSize: 13, marginTop: 5, whiteSpace: 'pre-wrap' }}>{e.bodyPreview}</div>}
                {e.detail && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 5, lineHeight: 1.5 }}>{e.detail}</div>}
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 5 }}>{fmtDateTime(e.createdAt)}</div>
              </div>
            );
          })}
        </div>
      )}

      {adminView === 'leads' && (
      <>
      {/* The view's own title and its one primary action. Add Lead used to sit
          in a white bar of its own under the filters; it belongs beside the
          heading, where every other view's primary action will sit too. */}
      <div className="qps-workspace-title-row">
        <div>
          <div className="qps-workspace-title">Work Order Status</div>
          <div className="qps-workspace-sub">
            {requests.length === 0
              ? 'Nothing has come in yet.'
              : `${requests.length} in total · emergencies and approvals sort to the top`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <ViewSwitch view={leadView} setView={pickLeadView} />
          <button type="button" className="qps-btn-primary" onClick={() => setModal('addLead')}>Add Lead</button>
        </div>
      </div>

      {/* Status filters. Underlined text rather than a second pill bar: two
          identical segmented controls stacked read as one control split in
          half, and moving between statuses is a smaller act than moving
          between views. */}
      {leadView === 'board' && (
        <LeadsBoard
          requests={requests}
          actionsFor={actionsFor}
          onOpen={openLeadDoc}
          onMove={moveLead}
        />
      )}

      {/* The board's columns ARE the statuses, so a status filter above them
          would be the same control twice. List and tiles keep it. */}
      <div style={{ display: leadView === 'board' ? 'none' : undefined }}>
      <div className="qps-filter-row" role="tablist" aria-label="Filter leads by status">
        {['all', 'new', 'estimate-sent', 'approved', 'assigned', 'completed', 'invoiced', 'paid'].map(t => {
          const label = t === 'all' ? 'All' : t === 'estimate-sent' ? 'Est. Sent' : t.charAt(0).toUpperCase() + t.slice(1);
          const count = requests.filter(r => (t === 'all' ? true : r.status === t)).length;
          return (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={activeTab === t}
              className={`qps-filter-btn ${activeTab === t ? 'active' : ''}`}
              onClick={() => setActiveTab(t)}
            >
              {label}
              <span className="qps-counter-badge">{count}</span>
            </button>
          );
        })}
      </div>

      {leadView === 'icons' && (
        <div className="lt">
          {visibleRequests.map(req => (
            <LeadCard key={req._id} req={req} variant="icon" actions={actionsFor(req)}
              onOpen={openLeadDoc} />
          ))}
        </div>
      )}

      <div style={{ display: leadView === 'list' ? undefined : 'none' }}>
        {visibleRequests.length === 0 && (
          <EmptyState
            title={activeTab === 'all' ? 'No work orders yet' : `Nothing at ${activeTab === 'estimate-sent' ? 'estimate sent' : activeTab}`}
            subtitle={activeTab === 'all'
              ? 'Every request from the site lands here, and you can enter one by hand from a phone call.'
              : 'Leads move through these stages as you work them. Try All to see everything.'}
            actionLabel={activeTab === 'all' ? 'Add Lead' : undefined}
            onAction={activeTab === 'all' ? () => setModal('addLead') : undefined}
          />
        )}
        {visibleRequests
          .map(req => (
            <SwipeToDelete key={req._id} onDelete={() => setConfirmDelete({ type: 'lead', id: req._id, label: `${req.homeownerId?.name || req.fullName}${req.serviceType ? ' · ' + req.serviceType : ''}` })}>
            <div
              className="lead-card"
              style={{ position: 'relative', cursor: 'pointer' }}
              // Clicking the row opens the document, the same as a board card.
              // Buttons inside it are excluded, or the three-line menu would
              // open the sheet behind itself on every tap.
              onClick={(e) => { if (!e.target.closest('button') && !e.target.closest('a')) openLeadDoc(req); }}
            >
              {/* Every action this job has, behind three lines at the top left.
                  The row of nine buttons that used to sit under each card was
                  unreadable, and the same list now serves the board and the
                  tiles — one place to add the next one to. */}
              <CardMenu
                variant="bars"
                label={`Actions for ${req.homeownerId?.name || req.fullName}`}
                items={actionsFor(req)}
              />
              <div className="lead-name" style={{ paddingRight: 30 }}>
                {req.homeownerId?.name || req.fullName}
                <span style={{ marginLeft: 8 }}>{statusTag(req.status)}</span>
                {/* How ready they are to buy, scored when the enquiry
                    landed. It decides which of these gets rung first. */}
                {req.lead?.band && (
                  <span className={`tag lead-${req.lead.band}`} style={{ marginLeft: 8 }}
                    title={[`${req.lead.score}/100`, ...(req.lead.signals || [])].join(' · ')}>
                    {req.lead.band === 'hot' ? 'Hot lead' : req.lead.band === 'warm' ? 'Warm' : 'Cold'}
                  </span>
                )}
                <span className={`urgency-dot ${urgencyDot(req.urgency)}`} style={{ marginLeft: 8 }}></span>
              </div>
              {/* Where this came from, when it came from a management company.
                  An emergency is red and first, because the whole point of the
                  priority is that it changes what Julian does next. A limit
                  that has been blown is amber and says DON'T DISPATCH — the
                  work is agreed but the money is not. */}
              {req.accountId && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                  {req.priority === 'emergency' && <span className="tag tag-red">Emergency</span>}
                  {req.priority === 'urgent' && <span className="tag tag-amber">Urgent</span>}
                  <span className="tag tag-purple">
                    {req.accountId?.name || 'Commercial account'}
                  </span>
                  {req.unitLabel && <span className="tag tag-slate">{req.unitLabel}</span>}
                  {req.nteStatus === 'exceeded' && (
                    <span className="tag tag-amber">Over limit — awaiting their approval</span>
                  )}
                  {req.nte?.amount != null && req.nteStatus !== 'exceeded' && (
                    <span className="tag tag-slate">Limit {fmtMoney(req.nte.amount)}</span>
                  )}
                  {/* Who they asked for. Only until somebody is actually on it
                      — once assigned, the assignment is the fact and the
                      request is history. */}
                  {req.requestedVendorName && !req.assignedVendorId && (
                    <span className="tag tag-blue">Asked for {req.requestedVendorName}</span>
                  )}
                </div>
              )}
              <div className="lead-job">{req.serviceType} · {req.serviceAddress}{req.zip ? `, ${req.zip}` : ''}</div>
              {/* Paperwork on the job — the move-out inspection a turn is
                  quoted against, a permit, a signed change. */}
              {(req.documents || []).length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '6px 0' }}>
                  {req.documents.map((d, i) => (
                    <a key={d._id || i} className="btn btn-outline btn-sm" href={d.url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                      {d.name || 'Document'}
                    </a>
                  ))}
                </div>
              )}
              <div style={{ fontSize: 12, color: 'var(--slate)', margin: '4px 0 6px' }}>
                {/* Older leads carry the literal string "undefined" from a
                    signed-in customer with no phone on their account; new ones
                    say "No phone given". Neither is something to ring. */}
                {req.phone && !['undefined', 'null', 'No phone given'].includes(req.phone)
                  ? <a href={`tel:${req.phone}`} style={{ color: 'var(--blue)', textDecoration: 'none' }}>{req.phone}</a>
                  : <span style={{ color: 'var(--amber)' }}>No phone given</span>}
                {req.email && <> · {req.email}</>}
              </div>
              {/* What they actually asked for, a line each with its own
                  photos. A lead with three jobs on it is three jobs to price
                  and three to check off, and the strip under each line is the
                  only way to tell which photo shows which. Leads taken before
                  lines existed have none and fall back to the description. */}
              {req.items?.length > 0 ? (
                <div style={{ margin: '0 0 8px' }}>
                  {req.items.map((it, i) => (
                    <div key={i} style={{ padding: '7px 0', borderTop: i ? '1px solid var(--border)' : 'none' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)' }}>
                        {req.items.length > 1 && <span style={{ color: 'var(--muted)' }}>{i + 1}. </span>}
                        {it.title || it.serviceType}
                        {it.serviceType && it.serviceType !== it.title && (
                          <span className="tag tag-slate" style={{ marginLeft: 6 }}>{it.serviceType}</span>
                        )}
                      </div>
                      {it.description && it.description !== it.title && (
                        <div style={{ fontSize: 13, color: 'var(--slate)', lineHeight: 1.4, marginTop: 2 }}>{it.description}</div>
                      )}
                      <MediaStrip urls={it.photos} label="Photos" />
                    </div>
                  ))}
                </div>
              ) : req.description && (
                <div style={{ fontSize: 13, color: 'var(--slate)', margin: '0 0 8px', lineHeight: 1.4 }}>{req.description}</div>
              )}
              <div className="lead-meta">
                <span className={`tag ${req.areaTag === 'Outside Area' ? 'tag-amber' : 'tag-green'}`}>{req.areaTag || 'Primary Area'}{req.city ? ` · ${req.city}` : ''}</span>
                <span className="tag tag-slate">via {req.leadSource || 'Website'}</span>
                <span className="tag tag-slate">{req.urgency}</span>
                {req.assignedVendorId && <span className="tag tag-teal">Tech: {req.assignedVendorId.businessName}</span>}
              </div>
              <div style={{ fontSize: 12, margin: '8px 0 2px', fontWeight: 600, color: req.appointmentAt ? 'var(--green)' : 'var(--amber)' }}>
                {req.appointmentAt ? `Appt: ${fmtDateTime(req.appointmentAt)}` : 'No appointment set'}
              </div>
              <div className="lead-footer">
                <span className="lead-amount">{req.estimate ? fmtMoney(req.estimate.amount) : '—'}</span>
                <span className="lead-date">{fmtDate(req.createdAt)}</span>
              </div>
              {/* Messaging stays on the card: it is the one thing Julian does
                  without deciding anything first, and burying it behind a menu
                  would cost a tap on every single job. Everything else lives
                  in the three lines at the top left. */}
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <MessageButton request={req} />
                {req.estimateStatus === 'pending' && (
                  <span style={{ fontSize: 12, color: 'var(--muted)' }}>Awaiting homeowner approval</span>
                )}
              </div>
              {/* Only when the lines didn't already show them — req.photos is
                  the flattened roll-up of exactly the same files. */}
              {!req.items?.some(it => it.photos?.length) && <MediaStrip urls={req.photos} label="Photos" />}
              <MediaStrip urls={req.completionPhotos} label="Completion photos" />
            </div>
            </SwipeToDelete>
          ))}
        {requests.filter(r => activeTab === 'all' ? true : r.status === activeTab).length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--muted)' }}>No requests in this category</div>
        )}
      </div>
      </div>
      </>
      )}

      {/* ── Projects ─────────────────────────────────────────── */}
      {adminView === 'projects' && activeProject && <AdminProjectDetail project={activeProject} />}

      {adminView === 'projects' && !activeProject && (
        <div>
          <div className="qps-workspace-title-row">
            <div>
              <div className="qps-workspace-title">Projects</div>
              <div className="qps-workspace-sub">
                {projects.length === 0 ? 'Nothing open.' : `${projects.length} open`}
              </div>
            </div>
            <button type="button" className="qps-btn-primary" onClick={() => openProjectForm(null)}>New Project</button>
          </div>

          {/* Estimates a tech has sent in and is waiting on */}
          {estimates.filter(e => e.status === 'submitted').length > 0 && (
            <>
              <div className="sec-label">Estimates waiting on you</div>
              {estimates.filter(e => e.status === 'submitted').map(e => (
                <div key={e._id} className="lead-card est-wait" style={{ cursor: 'default', borderLeft: '3px solid var(--amber)' }}>
                  {/* This read "{trade} · {vendor}", and a technician who
                      typed "Work" in the trade box got a card headed Work.
                      Whose job it is, then what the job is, then who sent
                      it — the same three lines the technician's own list
                      reads. */}
                  <div className="est-wait-top">
                    <div className="lead-name">
                      {docWho(e) || e.serviceAddress || docAbout(e, e.trade, 'en') || 'Estimate'}
                    </div>
                    <span className="tag tag-amber">Waiting on you</span>
                  </div>
                  <div className="lead-job">
                    {docAbout(e, e.trade, 'en')}
                    {e.vendorId?.businessName ? ` · from ${e.vendorId.businessName}` : ''}
                  </div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '8px 0' }}>
                    {[['Their price', e.techTotal, 'var(--navy)'], [`+${e.markupPercent}% markup`, e.markupAmount, 'var(--amber)'], ...(e.cardFeeAmount > 0 ? [['+ card fee', e.cardFeeAmount, 'var(--muted)']] : []), ['Client price', e.clientTotal, 'var(--green)']].map(([label, value, color]) => (
                      <div key={label} style={{ flex: 1, minWidth: 90 }}>
                        <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)' }}>{label}</div>
                        <div style={{ fontSize: 15, color }}>{fmtMoney(value)}</div>
                      </div>
                    ))}
                  </div>
                  {e.items?.map((i, idx) => (
                    <div key={idx} style={{ fontSize: 12, color: 'var(--slate)', display: 'flex', justifyContent: 'space-between' }}>
                      <span>{i.description}</span>
                      <span>{fmtMoney(i.total)}</span>
                    </div>
                  ))}
                  {/* Approving used to happen the instant the dropdown
                      changed, with the project list itself reading as a
                      label on the card. Pick, then press: adding a line to
                      a family's bill is not a thing to do by brushing a
                      select. */}
                  <div className="est-wait-act">
                    <label htmlFor={`approve-${e._id}`}>Approve onto</label>
                    <select
                      id={`approve-${e._id}`}
                      value={approveTo[e._id] || ''}
                      onChange={ev => setApproveTo(m => ({ ...m, [e._id]: ev.target.value }))}
                    >
                      <option value="">Which project?</option>
                      {projects.map(pr => <option key={pr._id} value={pr._id}>{pr.title}</option>)}
                    </select>
                    <button
                      type="button"
                      className="btn btn-green btn-sm"
                      disabled={!approveTo[e._id]}
                      onClick={() => handleApproveEstimateToProject(e, approveTo[e._id])}
                    >Approve</button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => handleDeclineEstimate(e)}
                    >Decline</button>
                  </div>
                  <div className="est-wait-note">
                    Approving adds it to that project&rsquo;s statement as one invoice line
                    at the client price, {fmtMoney(e.clientTotal)}. Declining sends the
                    technician your reason and nothing is billed.
                  </div>
                </div>
              ))}
            </>
          )}

          {projects.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px 20px', color: 'var(--muted)', lineHeight: 1.5 }}>
              No projects yet. A project is one property job made of many trades — create one, then add an invoice
              line per trade.
            </div>
          ) : projects.map(pr => (
            <div key={pr._id} className="lead-card" onClick={(ev) => { if (!ev.target.closest('button')) setActiveProject(pr); }}>
              <div className="lead-name">
                {pr.title}
                <span style={{ float: 'right', color: 'var(--muted)', fontSize: 12 }}>→</span>
              </div>
              <div className="lead-job">{pr.homeownerId?.name || pr.clientName || 'No client linked'}{pr.address ? ` · ${pr.address}` : ''}</div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', padding: '10px 0', borderTop: '1px solid var(--border)', margin: '8px 0 0' }}>
                {[['Invoiced', pr.invoicedTotal, 'var(--navy)'], ['Received', pr.receivedTotal, 'var(--green)'], ['Owed', pr.owedByClient, 'var(--red)'], ['Profit', pr.profit, pr.profit >= 0 ? 'var(--green)' : 'var(--red)']].map(([label, value, color]) => (
                  <div key={label} style={{ flex: 1, minWidth: 80 }}>
                    <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)' }}>{label}</div>
                    <div style={{ fontSize: 14, color }}>{fmtMoney(value)}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button className="btn btn-outline btn-sm" onClick={() => openProjectForm(pr)}>Edit</button>
                <button className="btn btn-ghost btn-sm" onClick={() => handleDeleteProject(pr)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Vendor Ledger ────────────────────────────────────── */}
      {adminView === 'ledger' && (
        <div>
          <PlatformRevenue />

          {/* The spreadsheet's "Total Amount Paid" / "Total Amount Owed" cells */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
            {[
              ['Total Invoiced', totals.invoiced, '#f1f5f9', 'var(--slate)'],
              ['Total Amount Paid', totals.paid, 'var(--green-light)', 'var(--green)'],
              ['Total Amount Owed', totals.owed, 'var(--red-light)', 'var(--red)'],
            ].map(([label, value, bg, color]) => (
              <div key={label} style={{ flex: 1, minWidth: 110, background: bg, borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color, opacity: .85 }}>{label}</div>
                <div style={{ fontSize: 19, color }}>{fmtMoney(value)}</div>
              </div>
            ))}
          </div>

          {blockedVendors.length > 0 && (
            <div className="alert alert-red">
              <div>
                <strong>{blockedVendors.length} vendor{blockedVendors.length === 1 ? '' : 's'} can't be paid — paperwork not current.</strong>
                <div style={{ marginTop: 4 }}>
                  {blockedVendors.map(v => `${v.businessName} (${v.compliance.blockers.join(', ')})`).join(' · ')}
                </div>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
            <button className="btn btn-primary btn-sm" onClick={() => openLedgerForm(null)}>+ New Invoice</button>
            <button className="btn btn-outline btn-sm" onClick={handleExportLedger}>Export CSV</button>
            <select value={ledgerVendorFilter} onChange={e => setLedgerVendorFilter(e.target.value)} style={{ maxWidth: 220, padding: '6px 10px', border: '1.5px solid var(--border)', borderRadius: 'var(--radius-sm)', background: 'var(--white)', color: 'var(--navy)', fontFamily: 'inherit' }}>
              <option value="">All vendors</option>
              {vendors.map(v => <option key={v._id} value={v._id}>{v.businessName}</option>)}
            </select>
          </div>

          {visibleLedger.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px 20px', color: 'var(--muted)', lineHeight: 1.5 }}>
              No ledger entries yet. Vendors create these when they submit an invoice, or add one yourself with + New Invoice.
            </div>
          ) : (
            visibleLedger.map(entry => (
              <LedgerCard
                key={entry._id}
                entry={entry}
                role="admin"
                onRecordPayment={openRecordPayment}
                onDeletePayment={handleDeletePayment}
                onAddProgress={openProgress}
                onEdit={openLedgerForm}
                onDelete={handleDeleteLedgerEntry}
                onOpenPaper={openInvoicePaper}
              />
            ))
          )}
        </div>
      )}

      {/* A vendor's own page: their assigned jobs and everything they've filed */}
      {adminView === 'techs' && activeVendor && (() => {
        // Re-read from `vendors` so the profile reflects the latest load.
        const v = vendors.find(x => x._id === activeVendor._id) || activeVendor;
        const p = v.profile || {};
        const phone = v.phone || v.userId?.phone;
        const email = v.email || v.userId?.email;
        const jobs = requests.filter(r => (r.assignedVendorId?._id || r.assignedVendorId) === v._id);
        const entries = ledger.filter(e => (e.vendorId?._id || e.vendorId) === v._id);
        const eTotals = ledgerTotals(entries);
        // Every progress report this vendor has filed, newest first, with the
        // invoice it belongs to.
        const updates = entries
          .flatMap(e => (e.progress || []).map(u => ({ ...u, entry: e })))
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        return (
          <div>
            <button className="btn btn-ghost btn-sm" style={{ marginBottom: 12 }} onClick={() => setActiveVendor(null)}>← All vendors</button>

            {/* The tech's details, read top to bottom with the label in front
                of every line — the same order Julian reads them off a phone
                call. Blank lines still show their label, so it's obvious at a
                glance what's missing rather than looking like it isn't asked. */}
            <div className="lead-card vsheet" style={{ cursor: 'default' }}>
              <div className="vsheet-head">
                {/* Text and Call, on the card itself.
                    Julian's most common action on a technician is texting
                    them, and it meant leaving the app to find the number.
                    These open his own phone's messaging and dialler, so they
                    work today with nothing configured — the in-app relay that
                    sends from the QPS line is a separate thing and is
                    currently off. */}
                {dialable(v) && (
                  <>
                    <a className="btn btn-outline btn-sm" href={smsHref(v)}>Text</a>
                    <a className="btn btn-outline btn-sm" href={`tel:${dialable(v)}`}>Call</a>
                  </>
                )}
                {/* A way back in, at any time.
                    The set-password link used to appear once, on the screen
                    that created the technician, and nowhere afterwards. With
                    email switched off that left Julian having added somebody
                    who could never sign in and no way to fix it. This mints a
                    fresh one and puts it on screen to text or read out. */}
                {(v.userId?._id || v.userId) && (
                  <button className="btn btn-outline btn-sm"
                    onClick={() => handleSendSignInLink(v.userId?._id || v.userId, v.businessName)}>
                    Sign-in link
                  </button>
                )}
                <button className="btn btn-outline btn-sm" onClick={() => openVendorEdit(v)}>Edit</button>
              </div>

              {[
                ['Name', v.userId?.name],
                ['Business Name', v.businessName],
                ['Occupation', p.occupation || v.specialty || tradesOf(v).join(', ')],
                ['Phone Number', phone && <a href={`tel:${phone}`}>{phone}</a>],
                ['Email Address', email && <a href={`mailto:${email}`}>{email}</a>],
                // Street, then "Provo, UT 84601" under it — the whole address
                // the record now holds, rather than the street on its own.
                ['Business Address', [p.address, [[p.city, p.state].filter(Boolean).join(', '), p.zip].filter(Boolean).join(' ')]
                  .map(x => String(x || '').trim()).filter(Boolean).join(', ')],
                ['Home Address if any', p.homeAddress],
              ].map(([label, value]) => (
                <div className="vsheet-row" key={label}>
                  <span className="vsheet-label">{label}:</span>
                  <span className={`vsheet-value${value ? '' : ' empty'}`}>{value || '—'}</span>
                </div>
              ))}

              {p.completedAt && (
                <div className="lead-meta" style={{ marginTop: 12 }}>
                  {p.yearsExperience && <span className="tag tag-slate">{p.yearsExperience} yrs experience</span>}
                  <span className={`tag ${p.hasTools === 'Yes' ? 'tag-green' : 'tag-amber'}`}>Tools: {p.hasTools || '—'}</span>
                  <span className={`tag ${p.licensed === 'Yes' ? 'tag-green' : 'tag-amber'}`}>Licensed: {p.licensed || '—'}</span>
                  <span className={`tag ${p.insured === 'Yes' ? 'tag-green' : 'tag-amber'}`}>Insured: {p.insured || '—'}</span>
                </div>
              )}
            </div>

            {/* ── The licence check ─────────────────────────────────
                The site tells property managers QPS verifies these. This is
                where that stops being a claim: the number, what DOPL showed,
                the date, and who looked. A tech with no check on file reads as
                "not checked yet" rather than as passing quietly. */}
            {(() => {
              const lv = v.licenseVerification || {};
              const st = v.licenseState || {};
              const passed = ['active', 'not-required'].includes(lv.result);
              const tone = !lv.checkedAt ? 'slate' : !passed ? 'red' : st.stale ? 'amber' : 'green';
              const words = {
                active: 'Active and in good standing',
                expired: 'Expired',
                suspended: 'Suspended or revoked',
                'not-found': 'No licence found under that number',
                'name-mismatch': 'Found under a different name',
                'not-required': 'No licence needed for the work they take',
              };
              return (
                <div className="lic-box" data-tone={tone}>
                  <div className="lic-head">
                    <span className="lic-title">Utah DOPL licence</span>
                    <button className="btn btn-outline btn-sm" onClick={() => openLicenseCheck(v)}>
                      {lv.checkedAt ? 'Check again' : 'Record a check'}
                    </button>
                  </div>
                  {!lv.checkedAt ? (
                    <div className="lic-line">Not checked yet. Nothing on file says anyone has looked.</div>
                  ) : (
                    <>
                      <div className="lic-line strong">{words[lv.result] || lv.result}</div>
                      <div className="lic-line">
                        {[lv.licenseNumber && `#${lv.licenseNumber}`, lv.licenseType].filter(Boolean).join(' · ') || 'No number recorded'}
                        {lv.licenseExpiresAt && ` · expires ${fmtDay(lv.licenseExpiresAt)}`}
                      </div>
                      <div className="lic-line">
                        Checked {fmtDay(lv.checkedAt)} by {lv.checkedByName || 'QPS'}
                        {lv.method === 'manual' ? ', by hand on the DOPL site' : ''}
                      </div>
                      {st.state === 'licence-expired' && <div className="lic-line warn">That licence has since expired. Check it again.</div>}
                      {st.state === 'old' && <div className="lic-line warn">Last looked at over a year ago.</div>}
                      {lv.note && <div className="lic-line note">{lv.note}</div>}
                    </>
                  )}
                </div>
              );
            })()}

            {/* ── Getting them paid ────────────────────────────────
                What the technician entered themselves. The last four only,
                until he asks for the rest — and asking is written onto their
                record, because Julian is holding other people's bank details
                and "who looked, and when" has to have an answer. */}
            {(() => {
              const pay = v.payout || {};
              const w9 = v.w9 || {};
              const shown = revealed && revealed.vendorId === String(v._id) ? revealed : null;
              if (!pay.method && !w9.signedAt) {
                return (
                  <div className="lic-box" data-tone="slate">
                    <div className="lic-head"><span className="lic-title">Paying them</span></div>
                    <div className="lic-line">Nothing on file. They enter their own bank details and sign their W-9 in the technician portal.</div>
                  </div>
                );
              }
              return (
                <div className="lic-box" data-tone={pay.method && w9.signedAt ? 'green' : 'amber'}>
                  <div className="lic-head"><span className="lic-title">Paying them</span></div>

                  {pay.method === 'ach' && (
                    <div className="lic-line">
                      <b>Bank transfer</b> · {pay.bankName || 'their bank'} ·{' '}
                      {pay.accountType === 'savings' ? 'savings' : 'checking'} ••••{pay.accountLast4}
                      <br />In the name of {pay.accountHolder}
                    </div>
                  )}
                  {pay.method === 'zelle' && <div className="lic-line"><b>Zelle</b> · {pay.zelleContact}</div>}
                  {pay.method === 'check' && (
                    <div className="lic-line"><b>Cheque</b> to {pay.checkPayableTo}<br />{pay.checkAddress}</div>
                  )}
                  {!pay.method && <div className="lic-line warn">No bank details yet.</div>}

                  <div className="lic-line">
                    {w9.signedAt
                      ? <>W-9 signed {fmtDay(w9.signedAt)} by {w9.name} · {w9.taxIdType === 'ein' ? 'EIN' : 'SSN'} •••••{w9.taxIdLast4}</>
                      : <span className="warn">No signed W-9.</span>}
                  </div>

                  <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                    {pay.method && (
                      <button className="btn btn-outline btn-sm" onClick={() => revealPayout(v._id)}>
                        Show full details
                      </button>
                    )}
                    {w9.signedAt && (
                      <button className="btn btn-outline btn-sm" onClick={() => revealW9(v._id)}>
                        Open the W-9
                      </button>
                    )}
                  </div>

                  {/* Revealed, and only while the panel is open. */}
                  {shown?.kind === 'payout' && (
                    <div className="reveal">
                      <div className="reveal-head">
                        <span>Full details</span>
                        <button className="btn btn-ghost btn-sm" onClick={() => setRevealed(null)}>Hide</button>
                      </div>
                      {shown.method === 'ach' && (
                        <>
                          <div className="reveal-row"><span>Routing</span><b>{shown.routingNumber}</b></div>
                          <div className="reveal-row"><span>Account</span><b>{shown.accountNumber}</b></div>
                          <div className="reveal-row"><span>Type</span><b>{shown.accountType}</b></div>
                          <div className="reveal-row"><span>Name on account</span><b>{shown.accountHolder}</b></div>
                        </>
                      )}
                      {shown.method === 'zelle' && <div className="reveal-row"><span>Zelle</span><b>{shown.zelleContact}</b></div>}
                      {shown.method === 'check' && (
                        <>
                          <div className="reveal-row"><span>Payable to</span><b>{shown.checkPayableTo}</b></div>
                          <div className="reveal-row"><span>Post to</span><b>{shown.checkAddress}</b></div>
                        </>
                      )}
                      <div className="reveal-note">This look has been written onto their record.</div>
                    </div>
                  )}

                  {shown?.kind === 'w9' && (
                    <div className="reveal">
                      <div className="reveal-head">
                        <span>W-9 · {shown.formRevision}</span>
                        <button className="btn btn-ghost btn-sm" onClick={() => setRevealed(null)}>Hide</button>
                      </div>
                      <div className="reveal-row"><span>Name</span><b>{shown.name}</b></div>
                      {shown.businessName && <div className="reveal-row"><span>Business</span><b>{shown.businessName}</b></div>}
                      <div className="reveal-row"><span>Taxed as</span><b>{shown.taxClassification}{shown.llcClassification ? ` (${shown.llcClassification})` : ''}</b></div>
                      <div className="reveal-row"><span>Address</span><b>{[shown.address, shown.city, shown.state, shown.zip].filter(Boolean).join(', ')}</b></div>
                      <div className="reveal-row">
                        <span>{shown.taxIdType === 'ein' ? 'EIN' : 'SSN'}</span>
                        <b>{shown.taxIdUnreadable ? 'Cannot be read with the current key' : shown.taxId}</b>
                      </div>
                      <div className="reveal-row"><span>Signed</span><b>{fmtDateTime(shown.signedAt)}</b></div>
                      <div className="reveal-row"><span>Typed name</span><b>{shown.signedName}</b></div>
                      {shown.signatureImage && (
                        <img className="reveal-sig" src={shown.signatureImage} alt={`Signature of ${shown.name}`} />
                      )}
                      <div className="reveal-note">
                        Signed from {shown.signedIp || 'an unrecorded address'}. This look has been written onto their record.
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Paperwork */}
            {v.compliance && (
              <div style={{ margin: '10px 0', padding: '10px 12px', borderRadius: 8, background: v.compliance.canBePaid ? 'var(--green-light)' : 'var(--red-light)' }}>
                <div style={{ fontSize: 12, marginBottom: 6, color: v.compliance.canBePaid ? 'var(--green)' : 'var(--red)' }}>
                  {v.compliance.canBePaid ? 'Clear to be paid' : 'Payments blocked — paperwork not current'}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {v.compliance.items.map(item => <span key={item.type}>{credentialTag(item)}</span>)}
                  {v.compliance.extras?.map(item => <span key={item._id}>{credentialTag(item)}</span>)}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  <button className="btn btn-outline btn-sm" onClick={() => openCredential({ type: 'insurance' }, v._id)}>Add / Renew a Document</button>
                </div>
              </div>
            )}

            {/* The documents themselves, not just their status. The pills
                above say insurance is valid; this is where you look at it.
                The "Application Doc 1" buttons that used to live in the row
                above are in here now, named and with a thumbnail, rather than
                numbered and anonymous. */}
            <VendorDocuments vendor={v} onOpenW9={v.w9?.signedAt ? () => revealW9(v._id) : undefined} />

            {/* Money with this vendor */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '14px 0' }}>
              {[
                ['Invoiced', eTotals.invoiced, '#f1f5f9', 'var(--slate)'],
                ['Paid', eTotals.paid, 'var(--green-light)', 'var(--green)'],
                ['Owed', eTotals.owed, 'var(--red-light)', 'var(--red)'],
              ].map(([label, value, bg, color]) => (
                <div key={label} style={{ flex: 1, minWidth: 100, background: bg, borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color, opacity: .85 }}>{label}</div>
                  <div style={{ fontSize: 18, color }}>{fmtMoney(value)}</div>
                </div>
              ))}
            </div>

            {/* Assigned jobs */}
            <div className="sec-label" style={{ marginTop: 16 }}>Assigned Jobs ({jobs.length})</div>
            {jobs.length === 0 ? (
              <div style={{ color: 'var(--muted)', fontSize: 13, padding: '8px 0 12px' }}>Nothing assigned to this vendor yet. Assign them from a lead in the Leads tab.</div>
            ) : jobs.map(r => (
              <div key={r._id} className="lead-card" style={{ cursor: 'default' }}>
                <div className="lead-name">
                  {r.serviceType}
                  <span style={{ marginLeft: 8 }}>{statusTag(r.status)}</span>
                </div>
                <div className="lead-job">{r.homeownerId?.name || r.fullName} · {r.serviceAddress}</div>
                {r.description && <div style={{ fontSize: 13, color: 'var(--slate)', margin: '6px 0' }}>{r.description}</div>}
                <div className="lead-footer">
                  <span className="lead-amount">{r.estimate ? fmtMoney(r.estimate.amount) : '—'}</span>
                  <span className="lead-date">{r.appointmentAt ? fmtDateTime(r.appointmentAt) : fmtDate(r.createdAt)}</span>
                </div>
                <MediaStrip urls={r.completionPhotos} label="Completion photos" />
              </div>
            ))}

            {/* Progress reports across every invoice */}
            <div className="sec-label" style={{ marginTop: 16 }}>Progress Reports ({updates.length})</div>
            {updates.length === 0 ? (
              <div style={{ color: 'var(--muted)', fontSize: 13, padding: '8px 0 12px' }}>No progress reports filed yet. Vendors post these against an invoice from their Ledger tab.</div>
            ) : (
              <div className="lead-card" style={{ cursor: 'default' }}>
                {updates.map((u, i) => (
                  <div key={u._id || i} style={{ paddingBottom: 12, marginBottom: 12, borderBottom: i < updates.length - 1 ? '1px dashed var(--border)' : 'none' }}>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                      {fmtDate(u.createdAt)} · {u.entry.invoiceNumber}
                      {u.percentComplete != null && <> · <span style={{ color: 'var(--green)' }}>{u.percentComplete}%</span></>}
                      {u.authorRole === 'admin' && <> · posted by QPS</>}
                    </div>
                    {u.note && <div style={{ fontSize: 13, margin: '3px 0 6px', lineHeight: 1.4 }}>{u.note}</div>}
                    <MediaStrip urls={u.photos} label="Photos" />
                  </div>
                ))}
              </div>
            )}

            {/* ── Their estimates, drafts and all ──────────────────────
                Support, not oversight. Half the calls QPS gets are a
                technician stuck on a document he has not sent anybody, and
                until this existed there was no way to look at it. Every row
                opens the same sheet he is looking at, with Edit on it. */}
            {(() => {
              const mine = techEstimates.vendorId === v._id ? techEstimates.list : [];
              const drafts = mine.filter(e => e.status === 'draft').length;
              return (
                <>
                  <div className="sec-label" style={{ marginTop: 16 }}>
                    Estimates ({mine.length}{drafts ? ` · ${drafts} still a draft` : ''})
                  </div>
                  {techEstimates.loading && techEstimates.vendorId === v._id ? (
                    <div style={{ color: 'var(--muted)', fontSize: 13, padding: '8px 0 12px' }}>Loading…</div>
                  ) : techEstimates.error ? (
                    <div className="alert alert-red" style={{ margin: '0 0 12px' }}>{techEstimates.error}</div>
                  ) : mine.length === 0 ? (
                    <div style={{ color: 'var(--muted)', fontSize: 13, padding: '8px 0 12px' }}>
                      This technician has not written an estimate yet.
                    </div>
                  ) : (
                    <div className="tp-rows adm-docs">
                      {mine.map(e => {
                        const share = e.share || {};
                        return (
                          <button key={e._id} className="tp-row" onClick={() => openEstimatePaper(e)}>
                            <div className="tp-row-main">
                              <div className="tp-row-name">{e.serviceAddress || e.trade || 'Untitled'}</div>
                              <div className="tp-row-meta">
                                {fmtDate(e.createdAt || e.submittedAt)}
                                {e.estimateNumber ? ` · ${e.estimateNumber}` : ''}
                                {/* What happened to the copy his own customer
                                    got, which is the thing he rings about. */}
                                {share.sentAt ? ` · sent to ${e.customer?.name || 'their customer'}` : ''}
                                {share.sentAt && !share.viewCount ? ' · not opened' : ''}
                                {share.decision === 'accepted' ? ' · approved' : ''}
                                {share.decision === 'declined' ? ' · declined' : ''}
                              </div>
                            </div>
                            <div className="tp-row-right">
                              <div className="tp-row-amt">{fmtMoney(e.techTotal)}</div>
                              {/* The same four tones the technician's own
                                  list uses, so one document does not change
                                  colour depending on who opened it. */}
                              <span className={`tp-chip tp-chip-${
                                e.status === 'approved' ? 'approved'
                                : e.status === 'rejected' ? 'declined'
                                : e.status === 'draft' ? 'draft' : 'pending'}`}>
                                {e.status === 'draft' ? 'Draft'
                                  : e.status === 'submitted' ? 'Waiting on QPS'
                                  : e.status === 'approved' ? 'Approved' : 'Declined'}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              );
            })()}

            {/* Their invoices */}
            <div className="sec-label" style={{ marginTop: 16 }}>Invoices ({entries.length})</div>
            {entries.length === 0 ? (
              <div style={{ color: 'var(--muted)', fontSize: 13, padding: '8px 0 40px' }}>No invoices from this vendor yet.</div>
            ) : entries.map(entry => (
              <LedgerCard
                key={entry._id}
                entry={entry}
                role="admin"
                onRecordPayment={openRecordPayment}
                onDeletePayment={handleDeletePayment}
                onAddProgress={openProgress}
                onEdit={openLedgerForm}
                onDelete={handleDeleteLedgerEntry}
                onOpenPaper={openInvoicePaper}
              />
            ))}
          </div>
        );
      })()}

      {/* ── Are uploads being kept? ────────────────────────────────────
          Render wipes the service's own disk on every deploy. If files are
          going there, every photograph a technician uploads is gone at the
          next restart — and because the thumbnails only break later, the
          first anybody knows is an invoice with no evidence behind it.
          Shown once, at the top, for as long as it is true. */}
      {storage?.fileStorage === 'local-disk' && (
        <div className="alert alert-red" style={{ margin: '0 0 16px' }}>
          <div>
            <strong>Photos and documents are not being kept.</strong>
            <div style={{ marginTop: 6, lineHeight: 1.55 }}>
              Files are saving to this server's own disk, which Render erases
              every time the site redeploys. Anything uploaded is lost at the
              next restart, and photos already uploaded this way are gone.
              Setting the three CLOUDINARY_ keys in Render fixes it for
              everything from then on.
            </div>
          </div>
        </div>
      )}

      {adminView === 'work' && <WorkEditor />}
      {adminView === 'chasing' && <FollowThrough />}
      {adminView === 'marketing' && <Marketing />}

      {adminView === 'techs' && !activeVendor && (
      <>
      <div className="qps-workspace-title-row">
        <div>
          <div className="qps-workspace-title">Vendors</div>
          <div className="qps-workspace-sub">
            {vendors.length === 0
              ? 'Nobody on the roster yet.'
              : `${vendors.length} on the roster` + (blockedVendors.length > 0
                  ? ` · ${blockedVendors.length} cannot be paid until their paperwork is current`
                  : ' · all paperwork current')}
          </div>
        </div>
        <button type="button" className="qps-btn-primary" onClick={openVendorAdd}>Add Vendor</button>
      </div>

        <div>
          {/* ── The same man, twice ──────────────────────────────────────
              Hangler was on the roster three times: once from signing
              himself up, twice more from being added by hand when the
              first attempt looked like it had not worked. Nothing stopped
              it, so his W-9 sat on one record, his invoice on another and
              his insurance on neither — which reads as a technician who
              never sent his paperwork.
              Adding a second record for a phone number already on the books
              is refused now. These are the ones from before. */}
          <DuplicateVendors vendors={vendors} ledger={ledger} onOpen={setActiveVendor} />

          {vendors.length > 0 && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              <button className={`btn btn-sm ${vendorLayout === 'board' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setVendorLayout('board')}>Board</button>
              <button className={`btn btn-sm ${vendorLayout === 'list' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setVendorLayout('list')}>List</button>
              <span style={{ fontSize: 12, color: 'var(--muted)', alignSelf: 'center' }}>
                {vendorLayout === 'board' ? 'Tap a card to open that vendor' : 'Swipe a row left to delete'}
              </span>
            </div>
          )}

          {/* Move card — the picker. Works on a phone, where dragging inside a
              side-scrolling board fights the scroll gesture. */}
          {moving && (
            <>
              <div className="modal active" style={{ alignItems: 'center' }}>
                <div className="modal-box" style={{ maxWidth: 420, borderRadius: 18, margin: '0 16px', padding: '22px 18px 26px' }}>
                  <div className="qps-sheet-title">
                    Move {moving.vendor.businessName}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16, lineHeight: 1.5 }}>
                    {moving.from && moving.from !== NO_TRADE
                      ? <>Filed under <strong style={{ color: 'var(--slate)' }}>{moving.from}</strong>. Picking a trade moves their card there. Everything else they do stays on their record.</>
                      : <>No trade on file. Picking a trade files them under it.</>}
                  </div>
                  <div style={{ maxHeight: '46vh', overflowY: 'auto', margin: '0 -4px' }}>
                    {TAXONOMY.map(x => x.cat)
                      .filter(cat => cat !== moving.from)
                      .map(cat => (
                        <button
                          key={cat}
                          className="cardmenu-item"
                          style={{ width: '100%', padding: '12px 12px', fontSize: 15 }}
                          onClick={() => {
                            handleMoveVendorTrade(moving.vendor, moving.from === NO_TRADE ? null : moving.from, cat);
                            setMoving(null);
                          }}
                        >
                          <span className="cardmenu-ico">{MenuIcons.move}</span>
                          <span>{cat}</span>
                        </button>
                      ))}
                  </div>
                  <button className="btn btn-ghost btn-lg" style={{ marginTop: 14 }} onClick={() => setMoving(null)}>Cancel</button>
                </div>
              </div>
              <div className="overlay active" onClick={() => setMoving(null)}></div>
            </>
          )}

          {vendorLayout === 'board' && vendors.length > 0 && (
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 12, fontSize: 12, color: 'var(--muted)' }}>
              {[
                ['var(--green)', 'Good to use'],
                ['var(--amber)', 'Renewal coming up'],
                ['var(--red)', "Can't use — paperwork not current"],
              ].map(([color, text]) => (
                <span key={text} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 9, height: 9, borderRadius: 50, background: color, display: 'inline-block' }} />
                  {text}
                </span>
              ))}
            </div>
          )}

          {/* Zero vendors is the normal state on launch day, not an error, and
              it is the screen Julian sits on while recruiting. It used to draw
              nothing at all — neither layout renders without a vendor — so the
              portal looked broken at exactly the moment it is working. */}
          {vendors.length === 0 && (
            <EmptyState
              title="No vendors yet"
              subtitle="Anyone who applies through the site lands here. You can also enter a trade you already work with by hand."
              actionLabel="Add Vendor"
              onAction={openVendorAdd}
            />
          )}

          {/* Trello-style board: a column per trade, a card per vendor */}
          {vendorLayout === 'board' && vendors.length > 0 && (
            <div className="vboard">
              {vendorColumns(vendors).map(([trade, { cards, also }]) => (
                <div
                  key={trade}
                  className={`vboard-col${dragOver === trade && dragging?.from !== trade ? ' drop-target' : ''}`}
                  onDragOver={(e) => { if (dragging && trade !== NO_TRADE) { e.preventDefault(); setDragOver(trade); } }}
                  onDragLeave={() => setDragOver(o => (o === trade ? null : o))}
                  onDrop={(e) => { e.preventDefault(); dropOnColumn(trade); }}
                >
                  <div className="vboard-head">
                    <span>{trade}</span>
                    <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      {['valid', 'expiring', 'blocked'].map(st => {
                        const n = cards.filter(v => (v.compliance?.status || 'blocked') === st).length;
                        if (!n) return null;
                        const color = { valid: 'var(--green)', expiring: 'var(--amber)', blocked: 'var(--red)' }[st];
                        return <span key={st} className="vboard-count" style={{ color }}>{n}</span>;
                      })}
                    </span>
                  </div>
                  <div className="vboard-list">
                    {cards.map(v => (
                      <VendorCard
                        key={v._id} v={v} column={trade}
                        onOpen={setActiveVendor} menuItems={menuItems}
                        dragging={dragging?.vendor?._id === v._id && dragging?.from === trade}
                        onDragStart={() => setDragging({ vendor: v, from: trade })}
                        onDragEnd={() => { setDragging(null); setDragOver(null); }}
                      />
                    ))}
                    {cards.length === 0 && also.length === 0 && <div className="vboard-empty">Nobody here yet</div>}

                    {/* Everyone else who does this work, by name only. They
                        are filed under their own trade and have a card there,
                        so a second full card here would be the duplicate this
                        board just stopped drawing. The name is enough to know
                        who to ring, and it opens them. */}
                    {also.length > 0 && (
                      <div className="vboard-also">
                        <div className="vboard-also-head">
                          {also.length} more can do this
                        </div>
                        {also.map(v => (
                          <button
                            key={v._id}
                            type="button"
                            className="vboard-also-row"
                            onClick={() => setActiveVendor(v)}
                            title={`Filed under ${homeTradeOf(v)}`}
                          >
                            <span className="vboard-also-dot" style={{ background: lightFor(v).edge }} />
                            <span className="vboard-also-name">{v.businessName}</span>
                            <span className="vboard-also-home">{shortTrade(homeTradeOf(v))}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {vendorLayout === 'list' && (vendors.length === 0 ? null : (
            vendors.map(v => {
              const p = v.profile || {};
              const done = !!p.completedAt;
              return (
                <SwipeToDelete key={v._id} onDelete={() => setConfirmDelete({ type: 'vendor', id: v._id, label: v.businessName })}>
                <div
                  className="lead-card"
                  style={{ position: 'relative' }}
                  onClick={(e) => { if (!e.target.closest('button, a')) setActiveVendor(v); }}
                  title="Open this vendor's profile"
                >
                  <CardMenu items={listMenuItems(v)} label={`Actions for ${v.businessName}`} />
                  <div className="lead-name" style={{ paddingRight: 30 }}>
                    {v.watched && <span className="vcard-watch" title="Watching">{MenuIcons.eye}</span>}
                    <span style={{ color: 'var(--blue)', textDecoration: 'underline' }}>{v.businessName}</span>
                    <span className={`tag ${done ? 'tag-green' : 'tag-amber'}`} style={{ marginLeft: 8 }}>{done ? 'Application complete' : 'Application pending'}</span>
                    <span style={{ float: 'right', color: 'var(--muted)', fontSize: 12 }}>→</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--slate)', margin: '4px 0' }}>
                    <a href={`tel:${v.phone || v.userId?.phone}`} style={{ color: 'var(--blue)', textDecoration: 'none' }}>{v.phone || v.userId?.phone || '—'}</a>
                    {' '}· <a href={`mailto:${v.email || v.userId?.email}`} style={{ color: 'var(--blue)', textDecoration: 'none' }}>{v.email || v.userId?.email}</a>
                    {p.address && <> · {p.address}</>}
                  </div>

                  {/* Insurance / license expiry — drives whether this vendor can be paid */}
                  {v.compliance && (
                    <div style={{ margin: '8px 0', padding: '8px 10px', borderRadius: 8, background: v.compliance.canBePaid ? 'var(--green-light)' : 'var(--red-light)' }}>
                      <div style={{ fontSize: 12, marginBottom: 6, color: v.compliance.canBePaid ? 'var(--green)' : 'var(--red)' }}>
                        {v.compliance.canBePaid ? 'Clear to be paid' : 'Payments blocked — paperwork not current'}
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {v.compliance.items.map(item => <span key={item.type}>{credentialTag(item)}</span>)}
                        {v.compliance.extras?.map(item => <span key={item._id}>{credentialTag(item)}</span>)}
                      </div>
                      <button className="btn btn-outline btn-sm" style={{ marginTop: 8 }} onClick={() => openCredential({ type: 'insurance' }, v._id)}>
                        Add / Renew a Document
                      </button>
                    </div>
                  )}

                  {done && (
                    <>
                      <div className="lead-meta" style={{ marginTop: 6 }}>
                        {p.yearsExperience && <span className="tag tag-slate">{p.yearsExperience} yrs experience</span>}
                        <span className={`tag ${p.hasTools === 'Yes' ? 'tag-green' : 'tag-amber'}`}>Tools: {p.hasTools || '—'}</span>
                        <span className={`tag ${p.licensed === 'Yes' ? 'tag-green' : 'tag-amber'}`}>Licensed: {p.licensed || '—'}</span>
                        <span className={`tag ${p.insured === 'Yes' ? 'tag-green' : 'tag-amber'}`}>Insured: {p.insured || '—'}</span>
                        {p.hasCompany === 'Yes' && <span className="tag tag-teal">Company{p.hasCrew === 'Yes' ? ' + crew' : ''}</span>}
                        {p.hasCompany !== 'Yes' && p.hasCrew === 'Yes' && <span className="tag tag-teal">Has crew</span>}
                      </div>
                      {p.expertise?.length > 0 && (
                        <div style={{ fontSize: 13, margin: '6px 0 2px' }}><strong>Expertise:</strong> {[...p.expertise, p.expertiseOther].filter(Boolean).join(', ')}</div>
                      )}
                      {p.skills?.length > 0 && (
                        <div style={{ fontSize: 13, margin: '2px 0' }}><strong>Skills:</strong> {[...p.skills, p.skillsOther].filter(Boolean).join(', ')}</div>
                      )}
                      {p.companyLink && (
                        <div style={{ fontSize: 13, margin: '2px 0' }}><strong>Link:</strong> <a href={p.companyLink} target="_blank" rel="noreferrer" style={{ color: 'var(--blue)' }}>{p.companyLink}</a></div>
                      )}
                      {p.documents?.length > 0 && (
                        <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                          {p.documents.map((d, i) => (
                            <button key={i} className="btn btn-outline btn-sm" onClick={() => window.open(d, '_blank')}>Document {i + 1}</button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  <div className="lead-footer">
                    <span className="tag tag-slate">Technician</span>
                    <span className="lead-date">Joined {fmtDate(v.createdAt)}</span>
                  </div>
                </div>
                </SwipeToDelete>
              );
            })
          ))}

          {vendors.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--muted)' }}>No technicians yet. They show up here when they sign up in the Technician Portal — or add one yourself with + Add Technician.</div>
          )}
        </div>
      </>
      )}

      {/* ── Commercial accounts ─────────────────────────────────────────
          The companies, their portfolios, and what is open on each. Their
          work orders also appear on the Leads board badged with the company
          name — this tab is for the relationship, that board is for the day. */}
      {adminView === 'accounts' && !adminAccountDetail && (
        <div>
          <button className="btn btn-primary" style={{ marginBottom: 16 }} onClick={openAccountForm}>
            + Add Commercial Account
          </button>
          {(adminAccounts || []).length === 0 ? (
            <div className="lead-card" style={{ cursor: 'default' }}>
              <div className="lead-job">No commercial accounts yet</div>
              <div style={{ fontSize: 13, color: 'var(--slate)', marginTop: 6, lineHeight: 1.5 }}>
                Property managers, HOAs and realtors can sign themselves up at
                qpshub.com/commercial — or add one yourself with the
                button above and send them a link to set their password.
              </div>
            </div>
          ) : (adminAccounts || []).map(a => (
            <div key={a.id} className="lead-card" onClick={() => openAdminAccount(a.id)}>
              <div className="lead-job">{a.name}</div>
              <div style={{ fontSize: 13, color: 'var(--slate)', margin: '4px 0 6px' }}>
                {a.phone && <a href={`tel:${a.phone}`} style={{ color: 'var(--blue)', textDecoration: 'none' }}>{a.phone}</a>}
                {a.email && <> · {a.email}</>}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <span className="tag tag-purple">{a.propertyCount} properties</span>
                {a.openWorkOrders > 0 && <span className="tag tag-amber">{a.openWorkOrders} open</span>}
                {a.defaultNteAmount != null && <span className="tag tag-slate">Limit {fmtMoney(a.defaultNteAmount)}</span>}
              </div>
              <div className="lead-footer">
                <span className="tag tag-slate">Commercial</span>
                <span className="lead-date">Joined {fmtDate(a.createdAt)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {adminView === 'accounts' && adminAccountDetail && !adminPropertyId && (() => {
        const d = adminAccountDetail;
        return (
          <div>
            <button className="btn btn-ghost btn-sm" style={{ marginBottom: 12 }} onClick={() => openAdminAccount(null)}>← All accounts</button>
            <div className="section-title">{d.account.name}</div>
            <div style={{ fontSize: 13, color: 'var(--slate)', marginBottom: 16 }}>
              {[d.account.phone, d.account.billingEmail || d.account.email].filter(Boolean).join(' · ')}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
              <div className="sec-label" style={{ marginBottom: 0 }}>Who can sign in</div>
              <button className="btn btn-outline btn-sm" onClick={() => openAccountMemberForm(d.account, null)}>+ Add someone</button>
            </div>
            {(d.members || []).length === 0 && (
              <div className="lead-card" style={{ cursor: 'default' }}>
                <div style={{ fontSize: 13, color: 'var(--slate)', lineHeight: 1.5 }}>
                  Nobody can sign in to this account yet. Add someone and they get a link to set their own password.
                </div>
              </div>
            )}
            {(d.members || []).map(m => (
              <div key={m.userId} className="lead-card" style={{ cursor: 'default' }}>
                <div className="lead-job">{m.name}</div>
                <div style={{ fontSize: 13, color: 'var(--slate)', marginTop: 4 }}>
                  {m.phone ? <a href={`tel:${m.phone}`} style={{ color: 'var(--blue)', textDecoration: 'none' }}>{m.phone}</a> : null}
                  {m.phone && m.email ? ' · ' : ''}{m.email}
                </div>
                <div className="lead-footer">
                  <span className={`tag tag-${m.level === 'owner' ? 'navy' : m.level === 'manager' ? 'blue' : 'slate'}`}>{m.level}</span>
                  <span style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-outline btn-sm" onClick={() => openAccountMemberForm(d.account, m)}>Access level</button>
                    <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }}
                      onClick={() => handleRemoveAccountMember(d.account._id || d.account.id, m)}>Remove</button>
                  </span>
                </div>
              </div>
            ))}

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, margin: '18px 0 10px' }}>
              <div className="sec-label" style={{ marginBottom: 0 }}>Properties ({(d.properties || []).length})</div>
              <button className="btn btn-outline btn-sm" onClick={() => openAdminPropertyForm(d.account)}>+ Add a property</button>
            </div>
            {/* Their portfolio is usually taken down the phone on the day they
                come on board, long before anyone at the company has signed in
                to add it themselves. */}
            {(d.properties || []).length === 0 && (
              <div className="lead-card" style={{ cursor: 'default' }}>
                <div style={{ fontSize: 13, color: 'var(--slate)', lineHeight: 1.5 }}>
                  No properties on this account yet. Add one here, or they can add their own — and import a
                  whole spreadsheet of them — once they sign in.
                </div>
              </div>
            )}
            {(d.properties || []).slice(0, 200).map(p => (
              <div key={p._id} className="lead-card" onClick={() => setAdminPropertyId(p._id)}>
                <div className="lead-job">{p.addressLine1}{p.city ? `, ${p.city}` : ''}{p.state ? ` ${p.state}` : ''}{p.zip ? ` ${p.zip}` : ''}</div>
                <div style={{ fontSize: 12.5, color: 'var(--slate)', marginTop: 4 }}>
                  {[p.label, p.externalId, (p.units || []).filter(u => u.isActive !== false).length ? `${(p.units || []).filter(u => u.isActive !== false).length} units` : ''].filter(Boolean).join(' · ')}
                </div>
                {(p.access?.gateCode || p.access?.lockboxCode) && (
                  <div style={{ fontSize: 12.5, color: 'var(--slate)', marginTop: 4 }}>
                    {p.access.gateCode ? `Gate ${p.access.gateCode}` : ''}
                    {p.access.gateCode && p.access.lockboxCode ? ' · ' : ''}
                    {p.access.lockboxCode ? `Lockbox ${p.access.lockboxCode}` : ''}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })()}

      {/* ── One property's own page ──────────────────────────────────────
          What has happened at this building, and where the next job starts.

          Most commercial work never arrives through the portal: Julian walks a
          turn with the manager and quotes it standing in the unit. So the two
          buttons here make a job and open the sheet for it in one motion — an
          estimate when there is a price to agree, an invoice when the work is
          done and there was never anything to agree. */}
      {adminView === 'accounts' && adminAccountDetail && adminPropertyId && (() => {
        const d = adminAccountDetail;
        const p = (d.properties || []).find(x => String(x._id) === String(adminPropertyId));
        // The property was removed, or the account was re-read and it is gone.
        // Falling back to the account beats a blank screen with no way out.
        if (!p) return (
          <div>
            <button className="btn btn-ghost btn-sm" onClick={() => setAdminPropertyId(null)}>← {d.account.name}</button>
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--muted)' }}>That property is no longer on this account.</div>
          </div>
        );

        const units = (p.units || []).filter(u => u.isActive !== false);
        // Every job ever raised at this building. The account detail already
        // carries them, so this is a filter rather than a second fetch.
        const jobs = (d.requests || []).filter(r => String(r.propertyId) === String(p._id));
        const where = [p.city, p.state].filter(Boolean).join(', ');

        return (
          <div>
            <button className="btn btn-ghost btn-sm" style={{ marginBottom: 12 }} onClick={() => setAdminPropertyId(null)}>← {d.account.name}</button>
            <div className="section-title">{p.addressLine1}{p.addressLine2 ? ` ${p.addressLine2}` : ''}</div>
            <div style={{ fontSize: 13, color: 'var(--slate)', marginBottom: 12 }}>
              {[where, p.zip].filter(Boolean).join(' ')}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
              {p.label && <span className="tag tag-slate">{p.label}</span>}
              {p.externalId && <span className="tag tag-slate">{p.externalId}</span>}
              <span className="tag tag-purple">{p.propertyType}</span>
              {units.length > 0 && <span className="tag tag-blue">{units.length} units</span>}
              {p.nteAmount != null && <span className="tag tag-amber">Limit {fmtMoney(p.nteAmount)}</span>}
            </div>

            {/* The codes, on the page rather than behind an edit form: this is
                what gets read down the phone to a tech sitting outside a gate. */}
            {(p.access?.gateCode || p.access?.lockboxCode || p.access?.pets || p.access?.parkingNotes) && (
              <div className="lead-card" style={{ cursor: 'default' }}>
                <div style={{ fontSize: 12.5, color: 'var(--slate)', lineHeight: 1.7 }}>
                  {p.access.gateCode && <div><strong>Gate</strong> {p.access.gateCode}</div>}
                  {p.access.lockboxCode && <div><strong>Lockbox</strong> {p.access.lockboxCode}</div>}
                  {p.access.pets && <div><strong>Pets</strong> {p.access.pets}</div>}
                  {p.access.parkingNotes && <div><strong>Parking</strong> {p.access.parkingNotes}</div>}
                </div>
              </div>
            )}

            {units.length > 0 && (
              <>
                <div className="sec-label" style={{ marginTop: 18 }}>Units ({units.length})</div>
                {units.map(u => (
                  <div key={u._id} className="lead-card" style={{ cursor: 'default' }}>
                    <div className="lead-job">#{u.unitNumber}</div>
                    <div style={{ fontSize: 12.5, color: 'var(--slate)', marginTop: 4 }}>
                      {[u.occupancy, u.tenantName, u.tenantPhone].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Files on the building: photos and video of what needs doing,
                the inspection PDF, receipts for anything bought for it. */}
            <div style={{ marginTop: 20 }}>
              <DocumentList
                title="Photos, video & files"
                documents={p.documents || []}
                units={units}
                onAdd={addAdminPropertyDocuments}
                onRemove={removeAdminPropertyDocument}
                emptyText="Nothing filed against this property yet. Photos, video, inspections, receipts — anything that belongs to the building rather than to one job."
              />
            </div>

            <button className="btn btn-primary" style={{ width: '100%', margin: '18px 0 8px' }}
              onClick={() => openPropertyJobForm(d.account, p, 'request')}>+ New service request</button>
            <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
              <button className="btn btn-outline btn-sm" style={{ flex: 1 }}
                onClick={() => openPropertyJobForm(d.account, p, 'estimate')}>+ New estimate</button>
              <button className="btn btn-outline btn-sm" style={{ flex: 1 }}
                onClick={() => openPropertyJobForm(d.account, p, 'invoice')}>+ New invoice</button>
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14, lineHeight: 1.5 }}>
              All three start a job at this property — the address, the codes and the spending limit come off
              this record, so all you type is what the work is. A service request just logs it; the other two
              go straight on to the price or the bill.
            </div>

            <div className="sec-label">Jobs here ({jobs.length})</div>
            {jobs.length === 0 && (
              <div className="lead-card" style={{ cursor: 'default' }}>
                <div style={{ fontSize: 13, color: 'var(--slate)', lineHeight: 1.5 }}>
                  Nothing has been quoted or billed at this property yet.
                </div>
              </div>
            )}
            {jobs.map(r => {
              const hasEstimate = r.estimate?.amount != null;
              const hasInvoice = r.invoice?.amount != null;
              return (
                <div key={r._id} className="lead-card" style={{ cursor: 'default', position: 'relative' }}>
                  {/* Top right, where a card's own delete belongs — and where
                      it is the same distance from every job on the page,
                      whether it was logged this morning or last spring. */}
                  <button
                    className="card-x"
                    aria-label={`Delete this service request — ${r.serviceType}`}
                    title="Delete this service request"
                    onClick={() => setConfirmDelete({
                      type: 'service request',
                      id: r._id,
                      label: [r.workOrder?.number, r.serviceType, r.unitLabel].filter(Boolean).join(' · '),
                    })}
                  >×</button>
                  <div className="lead-job" style={{ paddingRight: 28 }}>
                    {r.serviceType}{r.unitLabel ? ` · ${r.unitLabel}` : ''}
                    <span style={{ marginLeft: 8 }}>{statusTag(r.status)}</span>
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--slate)', margin: '4px 0 6px', lineHeight: 1.45 }}>
                    {r.workOrder?.number ? <strong>{r.workOrder.number} · </strong> : null}
                    {r.description}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {hasEstimate && (
                      <span className={`tag tag-${r.estimateStatus === 'approved' ? 'green' : 'blue'}`}>
                        Estimate {fmtMoney(r.estimate.amount)}
                      </span>
                    )}
                    {hasInvoice && (
                      <span className={`tag tag-${r.invoiceStatus === 'paid' ? 'green' : 'amber'}`}>
                        Invoice {fmtMoney(r.invoice.amount)}
                      </span>
                    )}
                    {r.nteStatus === 'exceeded' && <span className="tag tag-red">Over the limit</span>}
                  </div>
                  {/* Convert, not copy: the agreed price becomes the bill line
                      for line, and stays editable — the job that got done is
                      never exactly the job that got quoted. */}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                    <button className="btn btn-outline btn-sm" onClick={() => openEstimate(r)}>
                      {hasEstimate ? 'Estimate' : 'Write estimate'}
                    </button>
                    {/* The customer's way to pay, onto the clipboard, for a
                        text message. A project statement has had this button
                        all along; a job never did, so the only way a customer
                        ever got the link was the invoice email — and a bill
                        nobody can pay is the whole thing not working. */}
                    {hasInvoice && r.invoiceStatus !== 'paid' && (
                      <button className="btn btn-outline btn-sm" onClick={() => sharePayLink(r)}>Copy pay link</button>
                    )}
                    {hasEstimate && !hasInvoice && (
                      <button className="btn btn-primary btn-sm" onClick={() => openAdminInvoice(r, 'estimate')}>Convert to invoice</button>
                    )}
                    <button className="btn btn-outline btn-sm" onClick={() => openAdminInvoice(r)}>
                      {hasInvoice ? 'Invoice' : 'Write invoice'}
                    </button>
                    {/* The sheet the job is done from. Raising it and opening
                        it are one motion — whether it already exists is our
                        problem, not his — and it carries no prices. */}
                    <button className="btn btn-outline btn-sm" disabled={isRaisingWorkOrder} onClick={() => openWorkOrder(r)}>
                      {r.workOrder?.number || 'Create work order'}
                    </button>
                    {/* Raised on the wrong job, raised twice, or the job moved
                        on far enough that the sheet in the van is now wrong —
                        this takes it back without touching the job itself. */}
                    {r.workOrder?.number && (
                      <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)', borderColor: 'var(--red)' }}
                        onClick={() => setConfirmDelete({ type: 'work order', id: r._id, label: r.workOrder.number })}>
                        Delete WO
                      </button>
                    )}
                    <button className="btn btn-green btn-sm"
                      onClick={() => { setActiveRequest(r); setAssignVendorId(String(r.assignedVendorId?._id || r.assignedVendorId || r.requestedVendorId || '')); setModal('assign'); }}>
                      {r.assignedVendorId ? 'Reassign' : 'Assign technician'}
                    </button>
                  </div>

                  {/* Photos, video, receipts and PDFs against this job. The one
                      place an expense on a turn can actually be filed. */}
                  <div style={{ marginTop: 12 }}>
                    <DocumentList
                      documents={r.documents || []}
                      onAdd={(files, meta) => addJobDocuments(r, files, meta)}
                      emptyText="No photos, video or receipts on this job yet."
                    />
                  </div>
                  <div className="lead-footer">
                    <span className="lead-date">{fmtDate(r.createdAt)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* A client's own page — the Joist Profile screen. It takes over the
          Clients tab the way a vendor's page takes over Vendors. */}
      {adminView === 'clients' && activeClient && (() => {
        // Re-read from `clients` so an edit made on the page shows on it.
        const c = clients.find(x => x._id === activeClient._id) || activeClient;
        return (
          <ClientProfile
            client={c}
            projects={projects}
            requests={requests}
            onBack={() => setActiveClient(null)}
            onEdit={openClientForm}
            onAddLead={openLeadForClient}
            onOpenEstimate={openEstimate}
            onOpenProject={(project) => { setActiveClient(null); setAdminView('projects'); setActiveProject(project); }}
            onCreateEstimate={openEstimateForClient}
            onCreateInvoice={openClientInvoice}
            onInvoiceEstimate={openInvoiceFromEstimate}
            onPrintInvoice={openInvoiceDoc}
            onOpenJobInvoice={openJobInvoiceDoc}
            onDeleteEstimate={askDeleteEstimate}
            onReorderEstimates={reorderEstimates}
            onOpenInvoice={openStatementInvoice}
            onDeleteInvoice={askDeleteInvoice}
            onPromote={promoteClientToAccount}
          />
        );
      })()}

      {adminView === 'clients' && !activeClient && (
      <>
      <div className="qps-workspace-title-row">
        <div>
          <div className="qps-workspace-title">Clients</div>
          <div className="qps-workspace-sub">
            {clients.length === 0 ? 'Nobody signed up yet.' : `${clients.length} on the books`}
          </div>
        </div>
        <button type="button" className="qps-btn-primary" onClick={openAddClientForm}>Add Client</button>
      </div>

      <div>
          {clients.length === 0 ? (
            <EmptyState
              title="No clients yet"
              subtitle="Clients land here when they create an account on the site. Add one yourself for anyone you already work with."
              actionLabel="Add Client"
              onAction={openAddClientForm}
            />
          ) : (
            clients.map(c => {
              // Where the work is. The billing address is only worth a line
              // when it is somewhere else — an owner who lives out of state,
              // a rental billed to the landlord.
              const where = [c.address, c.address2, c.city, c.state, c.zip].filter(Boolean).join(', ');
              const billTo = [c.billingAddress, c.billingAddress2, c.billingCity, c.billingState, c.billingZip]
                .filter(Boolean).join(', ');
              return (
              <SwipeToDelete key={c._id} onDelete={() => setConfirmDelete({ type: 'client', id: c._id, label: c.name })}>
              {/* The whole card opens their page — everything billed, everything
                  owed, every estimate — which is what a name in a client list is
                  for. Edit stays on the card because correcting a phone number
                  taken down wrong shouldn't cost a screen. */}
              <div className="lead-card card-tappable" role="button" tabIndex={0}
                onClick={() => setActiveClient(c)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveClient(c); } }}>
                <div className="lead-name">{c.name}</div>
                <div style={{ fontSize: 12, color: 'var(--slate)', margin: '4px 0' }}>
                  {c.phone
                    ? <a href={`tel:${c.phone}`} onClick={e => e.stopPropagation()} style={{ color: 'var(--blue)', textDecoration: 'none' }}>{c.phone}</a>
                    : <span style={{ color: 'var(--amber)' }}>No phone given</span>}
                  {c.phone2 && <> · <a href={`tel:${c.phone2}`} onClick={e => e.stopPropagation()} style={{ color: 'var(--blue)', textDecoration: 'none' }}>{c.phone2}</a></>}
                  {c.email && <> · <a href={`mailto:${c.email}`} onClick={e => e.stopPropagation()} style={{ color: 'var(--blue)', textDecoration: 'none' }}>{c.email}</a></>}
                </div>
                {where && <div style={{ fontSize: 13, color: 'var(--slate)', margin: '2px 0' }}>{where}</div>}
                {billTo && (
                  <div style={{ fontSize: 12, color: 'var(--muted)', margin: '2px 0' }}>Bill to: {billTo}</div>
                )}
                {c.notes && (
                  <div style={{ fontSize: 12.5, color: 'var(--slate)', margin: '6px 0 0', lineHeight: 1.4 }}>{c.notes}</div>
                )}
                <div className="lead-footer">
                  <span className="tag tag-slate">Client</span>
                  <span className="lead-date">Joined {fmtDate(c.createdAt)}</span>
                </div>
                {/* A typo in a phone number used to mean deleting the client
                    and starting again. Putting an email on somebody who had
                    none is the same button, and hands you their portal link. */}
                <div style={{ marginTop: 10 }}>
                  <button className="btn btn-outline btn-sm"
                    onClick={e => { e.stopPropagation(); openClientForm(c); }}>Edit</button>
                </div>
              </div>
              </SwipeToDelete>
              );
            })
          )}
      </div>
      </>
      )}

      </div>{/* /qps-admin-workspace */}
    </div>
  );
}
